var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var _initialized;
const requestIdleCallback = typeof window.requestIdleCallback == "function" ? window.requestIdleCallback : setTimeout;
let _cachedFontSize = null;
window.addEventListener("resize", () => {
  _cachedFontSize = null;
}, { passive: true });
function getBaseFontSize() {
  return _cachedFontSize ?? (_cachedFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize));
}
function remToPx(rem) {
  return rem * getBaseFontSize();
}
class ResizeNotifier extends ResizeObserver {
  constructor(callback) {
    super((entries) => {
      if (__privateGet(this, _initialized)) return callback(entries, this);
      __privateSet(this, _initialized, true);
    });
    __privateAdd(this, _initialized, false);
  }
  disconnect() {
    __privateSet(this, _initialized, false);
    super.disconnect();
  }
}
_initialized = new WeakMap();
function formatMoney(cents, format = "{{amount_no_decimals_with_comma_separator}}") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const defaultFormat = format || "{{amount}}";
  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
    if (isNaN(number) || number == null) return "0";
    number = (number / 100).toFixed(precision);
    const parts = number.split(".");
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const cents2 = parts[1] ? decimal + parts[1] : "";
    return dollars + cents2;
  }
  switch (defaultFormat.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }
  return defaultFormat.replace(placeholderRegex, value);
}
function fetchConfig(type = "json", config = {}) {
  const headers = { "Content-Type": "application/json", Accept: `application/${type}`, ...config.headers };
  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }
  return {
    method: "POST",
    headers: (
      /** @type {HeadersInit} */
      headers
    ),
    body: config.body
  };
}
function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return (
    /** @type {T & { cancel(): void }} */
    debounced
  );
}
function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
  const animationPromises = animations.reduce(
    (acc, animation) => {
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }
      return acc;
    },
    /** @type {Promise<Animation>[]} */
    []
  );
  return Promise.allSettled(animationPromises).then(callback);
}
function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element) || event.target.getRootNode() !== document) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  return !element.contains(event.target);
}
function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
function prefersReducedMotion() {
  return reducedMotion.matches;
}
function resetShimmer(container = document.body) {
  if (!(container instanceof HTMLElement) || Shopify.designMode) {
    return;
  } else {
    const shimmer = container.querySelectorAll("[shimmer]");
    shimmer.forEach((item) => item.removeAttribute("shimmer"));
  }
}
class TextAnim extends HTMLElement {
  shimmer() {
    if (Shopify.designMode) return;
    this.setAttribute("shimmer", "");
  }
}
if (!customElements.get("text-anim")) {
  customElements.define("text-anim", TextAnim);
}
function onDocumentLoaded(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", callback);
  }
}
function isMobile() {
  return window.matchMedia && window.matchMedia("(max-width: 991px)").matches;
}
function isTablet() {
  return window.matchMedia && window.matchMedia("(max-width: 991px)").matches && window.matchMedia("(min-width: 768px)").matches;
}
function isDesktop() {
  return window.matchMedia && window.matchMedia("(min-width: 992px)").matches;
}
function isTouchDevice() {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}
const _tpl = document.createElement("template");
function setInnerHTML(element, html) {
  _tpl.innerHTML = html;
  const frag = _tpl.content;
  for (const old of frag.querySelectorAll("script")) {
    const fresh = document.createElement("script");
    for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
    fresh.textContent = old.textContent;
    old.replaceWith(fresh);
  }
  element.replaceChildren(frag);
}
function setCookie(name, value, days = 30, path = "/") {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=${path}; max-age=${maxAge}`;
}
function getCookie(name) {
  const cookieArr = document.cookie.split("; ");
  for (let cookie of cookieArr) {
    const [key, val] = cookie.split("=");
    if (decodeURIComponent(key) === name) {
      return decodeURIComponent(val);
    }
  }
  return null;
}
const _jumboCTX = window.__jumboCTX ?? (window.__jumboCTX = document.createElement("canvas").getContext("2d"));
const _hasLetterSpacing = "letterSpacing" in _jumboCTX;
function findOptimalFontSize(text, containerWidth, computedStyle) {
  const { fontStyle, fontWeight, fontFamily, letterSpacing } = computedStyle;
  const baseFontSize = parseFloat(computedStyle.fontSize);
  const lsPx = parseFloat(letterSpacing) || 0;
  const lsRatio = baseFontSize ? lsPx / baseFontSize : 0;
  let minSize = 1, maxSize = 2e3;
  while (maxSize - minSize > 0.5) {
    const midSize = (minSize + maxSize) / 2;
    _jumboCTX.font = `${fontStyle} ${fontWeight} ${midSize}px ${fontFamily}`;
    if (_hasLetterSpacing) _jumboCTX.letterSpacing = `${lsRatio * midSize}px`;
    if (_jumboCTX.measureText(text).width > containerWidth) {
      maxSize = midSize;
    } else {
      minSize = midSize;
    }
  }
  return minSize * 0.99;
}
let _hasInteracted = false;
let _pendingBroadEntries = /* @__PURE__ */ new Set();
const _interactionEvents = ["scroll", "wheel", "touchstart", "pointerdown", "keydown", "resize"];
function _onInteract() {
  if (_hasInteracted) return;
  _hasInteracted = true;
  _interactionEvents.forEach((evt) => window.removeEventListener(evt, _onInteract));
  const elements = Array.from(_pendingBroadEntries);
  for (const el of elements) {
    const fn = _lazyCbs.get(el);
    if (fn) {
      _unobserveAll(el);
      requestAnimationFrame(fn);
    }
  }
  _pendingBroadEntries.clear();
}
if (typeof window !== "undefined") {
  _interactionEvents.forEach((evt) => window.addEventListener(evt, _onInteract, { passive: true, once: true }));
}
let _lazyIO_strict = null;
let _lazyIO_broad = null;
const _lazyCbs = /* @__PURE__ */ new WeakMap();
function _unobserveAll(el) {
  _lazyIO_strict == null ? void 0 : _lazyIO_strict.unobserve(el);
  _lazyIO_broad == null ? void 0 : _lazyIO_broad.unobserve(el);
  _pendingBroadEntries.delete(el);
  _lazyCbs.delete(el);
}
function lazyInit(el, cb, margin = "200% 0px") {
  if (!_lazyIO_strict) {
    _lazyIO_strict = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const fn = _lazyCbs.get(entry.target);
          if (fn) {
            _unobserveAll(entry.target);
            requestAnimationFrame(fn);
          }
        }
      }
    }, { rootMargin: "10px 0px" });
    _lazyIO_broad = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (_hasInteracted) {
            const fn = _lazyCbs.get(entry.target);
            if (fn) {
              _unobserveAll(entry.target);
              requestAnimationFrame(fn);
            }
          } else {
            _pendingBroadEntries.add(entry.target);
          }
        } else {
          _pendingBroadEntries.delete(entry.target);
        }
      }
    }, { rootMargin: margin });
  }
  _lazyCbs.set(el, cb);
  _lazyIO_strict.observe(el);
  _lazyIO_broad.observe(el);
}
function onLazyLoad(e) {
  e.target.setAttribute("loaded", "");
  if (e.target.parentElement) e.target.parentElement.removeAttribute("data-lazy-loading");
}
function lazyReveal(scope = document) {
  const imgs = scope.querySelectorAll('img[loading="lazy"]:not([loaded])');
  if (!imgs.length) return;
  const toMark = [];
  const toWatch = [];
  for (const img of imgs) {
    if (img.complete && img.naturalWidth) toMark.push(img);
    else toWatch.push(img);
  }
  for (const img of toMark) img.setAttribute("loaded", "");
  for (const img of toWatch) {
    if (img.parentElement) img.parentElement.setAttribute("data-lazy-loading", "");
    img.addEventListener("load", onLazyLoad, { once: true });
  }
}
requestIdleCallback(() => lazyReveal());
export {
  ResizeNotifier as R,
  lazyReveal as a,
  resetShimmer as b,
  isDesktop as c,
  debounce as d,
  isTablet as e,
  fetchConfig as f,
  getCookie as g,
  isClickedOutside as h,
  isMobile as i,
  findOptimalFontSize as j,
  onDocumentLoaded as k,
  lazyInit as l,
  setInnerHTML as m,
  formatMoney as n,
  onAnimationEnd as o,
  prefersReducedMotion as p,
  remToPx as q,
  requestIdleCallback as r,
  setCookie as s,
  isTouchDevice as t
};
